import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaCookieBite } from "react-icons/fa";

const CONSENT_KEY = "cookieConsent";

function getStoredConsent() {
  try {
    const direct = localStorage.getItem(CONSENT_KEY);
    if (direct === "accepted" || direct === "declined") {
      return direct;
    }

    // Migrate legacy / per-user keys to a single browser-wide preference
    const legacy = localStorage.getItem("cookieConsent");
    if (legacy === "accepted" || legacy === "declined") {
      localStorage.setItem(CONSENT_KEY, legacy);
      return legacy;
    }

    const guest = localStorage.getItem("cookieConsent:guest");
    if (guest === "accepted" || guest === "declined") {
      localStorage.setItem(CONSENT_KEY, guest);
      return guest;
    }

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith("cookieConsent:") && !key.endsWith(":date")) {
        const value = localStorage.getItem(key);
        if (value === "accepted" || value === "declined") {
          localStorage.setItem(CONSENT_KEY, value);
          return value;
        }
      }
    }
  } catch (error) {
    console.warn("Error reading cookie consent:", error);
  }
  return null;
}

function saveConsent(value) {
  localStorage.setItem(CONSENT_KEY, value);
  localStorage.setItem(`${CONSENT_KEY}Date`, new Date().toISOString());
}

export default function CookieConsent() {
  const [show, setShow] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      const consent = getStoredConsent();
      setShow(consent !== "accepted" && consent !== "declined");
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const handleAccept = () => {
    saveConsent("accepted");
    setShow(false);
  };

  const handleDecline = () => {
    saveConsent("declined");
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[100] bg-white border-t-2 border-gray-200 shadow-lg animate-slide-up"
      role="dialog"
      aria-label="Cookie consent"
      aria-modal="false"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="flex-shrink-0">
            <FaCookieBite className="w-6 h-6 text-[#f26522]" />
          </div>

          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">
              We use cookies
            </h3>
            <p className="text-sm text-gray-600 mb-2">
              We use cookies to authenticate users, remember your preferences,
              and improve your experience. By clicking &quot;Accept All&quot;, you consent
              to our use of cookies.{" "}
              <Link
                to="/privacy"
                className="text-[#f26522] hover:underline font-medium"
              >
                Learn more
              </Link>
            </p>

            {!showDetails && (
              <button
                onClick={() => setShowDetails(true)}
                className="text-xs text-[#f26522] hover:underline"
              >
                View cookie details
              </button>
            )}

            {showDetails && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg text-xs text-gray-700 space-y-2">
                <div>
                  <strong>Essential Cookies:</strong> Required for the website
                  to function. These include authentication cookies that keep you
                  logged in. These cannot be disabled.
                </div>
                <div>
                  <strong>Preference Cookies:</strong> Remember your settings
                  and preferences (like theme, language) to provide a better
                  experience.
                </div>
                <div>
                  <strong>Local Storage:</strong> We use browser localStorage
                  to store your user data and preferences for faster loading.
                </div>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-[#f26522] hover:underline mt-2"
                >
                  Hide details
                </button>
              </div>
            )}
          </div>

          <div className="flex flex-row gap-2 flex-shrink-0 w-full sm:w-auto">
            <button
              onClick={handleAccept}
              className="flex-1 sm:flex-none px-4 py-2 bg-[#f26522] hover:bg-[#d95518] text-white text-sm font-semibold rounded transition-colors whitespace-nowrap"
            >
              Accept All
            </button>
            <button
              onClick={handleDecline}
              className="flex-1 sm:flex-none px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-semibold rounded transition-colors whitespace-nowrap"
            >
              Decline
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
